import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce, useObservable } from 'react-use';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import box from '~/assets/icons/box_2.svg';
import chest from '~/assets/icons/chest.svg';
import star from '~/assets/icons/four_final_star.svg';
import hand from '~/assets/icons/hand.svg';
import { playSound, Sound } from '~/assets/sounds';
import { ClothesImage } from '~/components/collections/ClothesImage';
import { CollectionImage } from '~/components/collections/CollectionImage';
import { CurrencyIcon } from '~/components/collections/CurrencyIcon';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { ImgIcon } from '~/components/ui/ImgIcon';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks/useWalletIcon';
import { rage } from '~/rage';
import {
	CaseItemRarity,
	Currency,
	isClothesItem,
	isSecretRewardByRarity,
	numWord,
	ParseRewardValueTypeMap,
	RewardType,
} from '~/shared';
import cardSoft from '../../assets/images/cases/card_soft.png';
import {
	setIsOpeningCase,
	setOpenCaseResult,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { CaseItemColors } from '../CaseItem';
import {
	StyledBackButtonContainer,
	StyledButtonContainer,
	StyledButtonContent,
	StyledCenterArrows,
	StyledContainer,
	StyledCount,
	StyledExpContainer,
	StyledFlipContainer,
	StyledImageContainer,
	StyledItem,
	StyledItemsContainer,
	StyledItemTopContainer,
	StyledLight,
	StyledRewardButtonsGrid,
	StyledRewardShadow,
	StyledSecretReward,
	StyledShadow,
	StyledShadowCenter,
	StyledStarBg,
} from './ShopOpenCaseScroll.styled';

interface ICaseScrollItem {
	id: number;
	type: RewardType;
	color: string;
	isWinningItem: boolean;
	name: string;
	itemValue: unknown;
	isSecret?: boolean;
}

function SecretRewardCard({
	item,
	showReward,
	isFlipped,
	tiltTransform,
	getLayerTransform,
	glowAnimVars,
	amount,
}: {
	item: ICaseScrollItem;
	showReward: boolean;
	isFlipped: boolean;
	tiltTransform?: string;
	getLayerTransform: (
		intensity: number,
		centerType?: 'none' | 'both' | 'x',
	) => string | undefined;
	glowAnimVars: React.CSSProperties;
	amount: number;
}) {
	return (
		<StyledFlipContainer
			$isFlipped={isFlipped}
			style={{
				transform: tiltTransform,
				transition: 'transform 0.1s ease-out',
			}}
		>
			<div className="flip-inner">
				<div className="flip-front">
					<StyledSecretReward />
				</div>
				<div className="flip-back">
					<StyledItem
						$isDimmed={showReward && !item.isWinningItem}
						$borderColor={
							item.isWinningItem && showReward ? item.color : undefined
						}
					>
						{item.isWinningItem && showReward && <StyledLight />}
						<StyledShadow
							$animate={item.isWinningItem && showReward}
							style={{
								backgroundColor: item.color,
								transform: getLayerTransform(0.3, 'none'),
								transition: 'transform 0.1s ease-out',
								...glowAnimVars,
							}}
						/>
						<StyledShadowCenter
							$animate={item.isWinningItem && showReward}
							style={{
								transform: getLayerTransform(0.2, 'both'),
								transition: 'transform 0.1s ease-out',
								...glowAnimVars,
							}}
						/>
						<StyledStarBg
							$animate={item.isWinningItem && showReward}
							style={{
								transform: getLayerTransform(0.4, 'both'),
								transition: 'transform 0.1s ease-out',
								...glowAnimVars,
							}}
						/>
						<StyledItemTopContainer
							style={{
								transform: getLayerTransform(0.5, 'none'),
								transition: 'transform 0.1s ease-out',
							}}
						>
							<div className="dot" style={{ backgroundColor: item.color }} />
							<Text>{item.name}</Text>
						</StyledItemTopContainer>
						<StyledImageContainer
							$type={item.type}
							style={{
								transform: getLayerTransform(0.6, 'x'),
								transition: 'transform 0.1s ease-out',
							}}
						>
							{item.type === RewardType.VEHICLE &&
							item.itemValue &&
							typeof item.itemValue === 'string' ? (
								<CollectionImage
									collection={'vehicles'}
									id={item.itemValue}
									size={'md'}
								/>
							) : null}
							{item.type === RewardType.ITEM &&
							item.itemValue &&
							typeof item.itemValue === 'string' &&
							isClothesItem(item.itemValue) ? (
								<ClothesImage id={item.itemValue} size={180} />
							) : null}
							{item.type === RewardType.ITEM &&
							item.itemValue &&
							typeof item.itemValue === 'string' &&
							!isClothesItem(item.itemValue) ? (
								<CollectionImage
									collection={'items'}
									id={item.itemValue}
									size={180}
								/>
							) : null}
							{item.type === RewardType.MONEY && (
								<img src={cardSoft} width={160} height={160} />
							)}
							{item.type === RewardType.EXP && (
								<StyledExpContainer>
									<ImgIcon icon={star} width={30} height={30} color="#FFB500" />
									<Text>{amount.toLocaleString('ru-RU')}</Text>
								</StyledExpContainer>
							)}
						</StyledImageContainer>
					</StyledItem>
				</div>
			</div>
		</StyledFlipContainer>
	);
}

const ShopOpenCaseScroll = ({ rewardId }: { rewardId: string | null }) => {
	const dispatch = useLayoutDispatch();
	const activeCase = useLayoutSelector((state) => state.mainMenu.activeCase);
	const [items, setItems] = useState<ICaseScrollItem[]>([]);
	const [winningIndex, setWinningIndex] = useState<number | null>(null);
	const [translateX, setTranslateX] = useState('-50%');
	const [transitionTime, setTransitionTime] = useState(0);
	const [isOpening, setIsOpening] = useState(false);
	const [isScrolling, setIsScrolling] = useState(false);
	const [showReward, setShowReward] = useState(false);
	const [instantMode, setInstantMode] = useState(false);
	const [isFlipped, setIsFlipped] = useState(false);
	const [hasAutoOpened, setHasAutoOpened] = useState(false);
	const [transitionEasing, setTransitionEasing] = useState(
		'cubic-bezier(.14,.64,0,1)',
	);
	const [tiltStyle, setTiltStyle] = useState({
		rotateX: 0,
		rotateY: 0,
		scale: 1,
		mouseX: 0,
		mouseY: 0,
	});
	const [glowAnimVars, setGlowAnimVars] = useState<React.CSSProperties>({});
	const donateIcon = useWalletIcon(Currency.Donate, true);

	const scrollTimeout1Ref = useRef<number | null>(null);
	const scrollTimeout2Ref = useRef<number | null>(null);
	const itemsContainerRef = useRef<HTMLDivElement>(null);
	const rafIdRef = useRef<number | null>(null);
	const lastCrossingRef = useRef<number>(0);
	const initialTxRef = useRef<number | null>(null);

	const handleOpen = useCallback(async () => {
		if (scrollTimeout1Ref.current) {
			clearTimeout(scrollTimeout1Ref.current);
			scrollTimeout1Ref.current = null;
		}
		if (scrollTimeout2Ref.current) {
			clearTimeout(scrollTimeout2Ref.current);
			scrollTimeout2Ref.current = null;
		}

		setInstantMode(false);
		setIsOpening(true);
		setIsScrolling(true);
		setShowReward(false);
		setIsFlipped(false);
		setTransitionTime(0);
		setTransitionEasing('cubic-bezier(.1,.9,0,1)');

		const winningItem = rewardId;

		if (!winningItem) {
			setIsOpening(false);
			setIsScrolling(false);
			return;
		}

		const newItems = Array.from({ length: 201 }, (_, i) => {
			const item =
				activeCase?.items[
					Math.floor(Math.random() * (activeCase?.items.length || 1))
				];
			const donateItemId = item?.donateItemId || '';
			const rewardType = rage.inventoryDonate.getTypeById(donateItemId);
			const imageId = rage.cases.getRewardImageId(donateItemId);

			return {
				id: i,
				type: rewardType,
				color: CaseItemColors[item?.rarity || CaseItemRarity.COMMON],
				isWinningItem: false,
				name: rage.cases.getRewardNameById(donateItemId),
				itemValue: imageId ?? donateItemId,
				isSecret: false,
			};
		});
		const winIndex = 181;
		newItems[winIndex].isWinningItem = true;
		newItems[winIndex].color =
			CaseItemColors[
				activeCase?.items.find((item) => item.donateItemId === winningItem)
					?.rarity || CaseItemRarity.COMMON
			];
		newItems[winIndex].type = rage.inventoryDonate.getTypeById(
			winningItem || '',
		);
		newItems[winIndex].name = rage.cases.getRewardNameById(winningItem || '');
		newItems[winIndex].itemValue =
			rage.cases.getRewardImageId(winningItem) ?? winningItem;
		const winRarity =
			activeCase?.items.find((item) => item.donateItemId === winningItem)
				?.rarity ?? CaseItemRarity.COMMON;
		newItems[winIndex].isSecret = isSecretRewardByRarity(winRarity);
		setItems(newItems);
		setWinningIndex(winIndex);

		const itemWidth = 380; // ширина предмета
		const itemMargin = 10; // расстояние между предметами
		const totalItemWidth = itemWidth + itemMargin;
		const targetX = (winIndex - 100) * totalItemWidth;
		const jitter = Math.floor(Math.random() * 380) - 190;
		setTranslateX('-50%');
		requestAnimationFrame(() => {
			setTransitionTime(6);
			setTranslateX(`calc(-50% - ${targetX + jitter}px)`);
		});
		scrollTimeout1Ref.current = window.setTimeout(() => {
			setTransitionTime(2);
			setTransitionEasing('cubic-bezier(.05,.95,0,1)');
			setTranslateX(`calc(-50% - ${targetX}px)`);
			scrollTimeout2Ref.current = window.setTimeout(() => {
				setIsScrolling(false);
				setShowReward(true);
				playSound(
					isSecretRewardByRarity(winRarity)
						? Sound.SuperReward
						: Sound.LowReward,
				);
				setGlowAnimVars({
					'--glow-dur': `${2 + Math.random() * 0.8}s`,
					'--glow-delay': `${Math.random() * 0.3}s`,
					'--glow-o-min': 0.6 + Math.random() * 0.1,
					'--glow-o-max': 0.95 + Math.random() * 0.1,
					'--glow-blur-min': `${4 + Math.random() * 2}px`,
					'--glow-blur-max': `${8 + Math.random() * 4}px`,
					'--reward-dur': `${2.4 + Math.random() * 0.6}s`,
					'--reward-delay': `${Math.random() * 0.2}s`,
					'--reward-scale-min': 0.96 + Math.random() * 0.04,
					'--reward-scale-max': 1.02 + Math.random() * 0.03,
					'--reward-o-min': 0.6 + Math.random() * 0.15,
					'--reward-o-max': 0.95 + Math.random() * 0.1,
				} as React.CSSProperties);
				if (isSecretRewardByRarity(winRarity)) {
					setTimeout(() => setIsFlipped(true), 500);
				}
			}, 2000);
		}, 6000);
	}, [activeCase?.items, rewardId]);

	const handleOpenCase = useCallback(async () => {
		if (!activeCase?.id) {
			return;
		}

		const nextRewardId = await rage.cases.openLootbox(activeCase.id);
		dispatch(setOpenCaseResult(nextRewardId));
		dispatch(setIsOpeningCase(true));
		setHasAutoOpened(false);
	}, [activeCase?.id, dispatch]);

	const handleFastScroll = useCallback(() => {
		if (!winningIndex) {
			return;
		}

		if (scrollTimeout1Ref.current) {
			clearTimeout(scrollTimeout1Ref.current);
			scrollTimeout1Ref.current = null;
		}
		if (scrollTimeout2Ref.current) {
			clearTimeout(scrollTimeout2Ref.current);
			scrollTimeout2Ref.current = null;
		}

		setInstantMode(true);
		setTransitionTime(0);
		const itemWidth = 380;
		const itemMargin = 10;
		const totalItemWidth = itemWidth + itemMargin;
		const targetX = (winningIndex - 100) * totalItemWidth;
		setTranslateX(`calc(-50% - ${targetX}px)`);

		setIsScrolling(false);
		setShowReward(true);
		setGlowAnimVars({
			'--glow-dur': `${2 + Math.random() * 0.8}s`,
			'--glow-delay': `${Math.random() * 0.3}s`,
			'--glow-o-min': 0.6 + Math.random() * 0.1,
			'--glow-o-max': 0.95 + Math.random() * 0.1,
			'--glow-blur-min': `${4 + Math.random() * 2}px`,
			'--glow-blur-max': `${8 + Math.random() * 4}px`,
			'--reward-dur': `${2.4 + Math.random() * 0.6}s`,
			'--reward-delay': `${Math.random() * 0.2}s`,
			'--reward-scale-min': 0.96 + Math.random() * 0.04,
			'--reward-scale-max': 1.02 + Math.random() * 0.03,
			'--reward-o-min': 0.6 + Math.random() * 0.15,
			'--reward-o-max': 0.95 + Math.random() * 0.1,
		} as React.CSSProperties);

		const winRarity =
			activeCase?.items.find((item) => item.donateItemId === rewardId)
				?.rarity ?? CaseItemRarity.COMMON;
		playSound(
			isSecretRewardByRarity(winRarity) ? Sound.SuperReward : Sound.LowReward,
		);
		if (isSecretRewardByRarity(winRarity)) {
			setTimeout(() => setIsFlipped(true), 300);
		}
	}, [winningIndex, rewardId, activeCase?.items]);

	const donateItems = useObservable(rage.inventoryDonate.getDonateOwnedItems());
	const caseDonateId = activeCase?.donateItemId ?? activeCase?.id;
	const ownedCount = useMemo(
		() => donateItems?.find((item) => item.id === caseDonateId)?.count || 0,
		[donateItems, caseDonateId],
	);

	const containerRef = useRef<HTMLDivElement>(null);

	const handleGlobalMouseMove = useCallback(
		(event: MouseEvent) => {
			if (!showReward || !winningIndex || !containerRef.current) return;

			const containerRect = containerRef.current.getBoundingClientRect();

			const cardCenterX = containerRect.left + containerRect.width / 2;
			const cardCenterY = containerRect.top + containerRect.height / 2 - 40;

			const mouseX = event.clientX - cardCenterX;
			const mouseY = event.clientY - cardCenterY;

			const cardWidth = 380;
			const cardHeight = 260;

			const rotateY = (mouseX / (cardWidth / 2)) * 3;
			const rotateX = -(mouseY / (cardHeight / 2)) * 3;

			const normalizedX = mouseX / (cardWidth / 2);
			const normalizedY = mouseY / (cardHeight / 2);

			setTiltStyle({
				rotateX,
				rotateY,
				scale: 1.02,
				mouseX: normalizedX,
				mouseY: normalizedY,
			});
		},
		[showReward, winningIndex],
	);

	const handleTakeReward = useCallback(async () => {
		if (!rewardId) {
			return;
		}

		try {
			await rage.inventoryDonate.useReward(rewardId);
			const rewardName = rage.cases.getRewardNameById(rewardId);
			rage.notifications.showSuccess(
				`Вы успешно получили награду "${rewardName}"`,
			);
		} catch (error) {
			console.error('Ошибка при получении награды:', error);
			return;
		}

		setShowReward(false);
		setIsOpening(false);
		// setIsScrolling(false);
		setTransitionTime(0);
		setTranslateX('-50%');
		setTiltStyle({ rotateX: 0, rotateY: 0, scale: 1, mouseX: 0, mouseY: 0 });

		dispatch(setOpenCaseResult(null));
		if (!ownedCount) {
			dispatch(setIsOpeningCase(false));
		}
	}, [rewardId, ownedCount, dispatch]);

	const handleSkipReward = useCallback(async () => {
		if (!rewardId) {
			return;
		}

		setShowReward(false);
		setIsOpening(false);
		// setIsScrolling(false);
		setTransitionTime(0);
		setTranslateX('-50%');
		setTiltStyle({ rotateX: 0, rotateY: 0, scale: 1, mouseX: 0, mouseY: 0 });
		dispatch(setOpenCaseResult(null));
		if (!ownedCount) {
			dispatch(setIsOpeningCase(false));
		}
	}, [rewardId, ownedCount, dispatch]);

	const handleSellReward = useCallback(async () => {
		if (!rewardId) {
			return;
		}

		await rage.inventoryDonate.sellReward(rewardId);
		setShowReward(false);
		setIsOpening(false);
		setTransitionTime(0);
		setTranslateX('-50%');
		setTiltStyle({ rotateX: 0, rotateY: 0, scale: 1, mouseX: 0, mouseY: 0 });
		dispatch(setOpenCaseResult(null));
		if (!ownedCount) {
			dispatch(setIsOpeningCase(false));
		}
	}, [rewardId, ownedCount]);

	useEffect(() => {
		setHasAutoOpened(false);
	}, [rewardId]);

	useEffect(() => {
		if (isOpening || hasAutoOpened) {
			return;
		}

		if (!rewardId || !activeCase?.items?.length) {
			return;
		}

		handleOpen().catch((err) => rage.notifications.showException(err));
		setHasAutoOpened(true);
	}, [
		isOpening,
		hasAutoOpened,
		handleOpen,
		rewardId,
		activeCase?.items?.length,
	]);

	useEffect(() => {
		if (!showReward) {
			setTiltStyle({ rotateX: 0, rotateY: 0, scale: 1, mouseX: 0, mouseY: 0 });
			return;
		}

		window.addEventListener('mousemove', handleGlobalMouseMove);

		return () => {
			window.removeEventListener('mousemove', handleGlobalMouseMove);
		};
	}, [showReward, handleGlobalMouseMove]);

	useEffect(() => {
		if (!isScrolling) {
			if (rafIdRef.current) {
				cancelAnimationFrame(rafIdRef.current);
				rafIdRef.current = null;
			}
			initialTxRef.current = null;
			lastCrossingRef.current = 0;
			return;
		}

		const itemWidth = 390;
		const halfCard = 190;

		const tick = () => {
			const el = itemsContainerRef.current;
			if (!el) {
				rafIdRef.current = requestAnimationFrame(tick);
				return;
			}

			const matrix = new DOMMatrix(getComputedStyle(el).transform);
			const tx = matrix.m41;

			if (initialTxRef.current === null) {
				initialTxRef.current = tx;
			}

			const offset = initialTxRef.current - tx;
			const crossing = Math.floor((offset + halfCard) / itemWidth);

			if (crossing !== lastCrossingRef.current) {
				playSound(Sound.LinkCursor2);
				lastCrossingRef.current = crossing;
			}

			rafIdRef.current = requestAnimationFrame(tick);
		};

		rafIdRef.current = requestAnimationFrame(tick);

		return () => {
			if (rafIdRef.current) {
				cancelAnimationFrame(rafIdRef.current);
				rafIdRef.current = null;
			}
		};
	}, [isScrolling]);

	useEffectOnce(() => {
		return () => {
			if (scrollTimeout1Ref.current) {
				clearTimeout(scrollTimeout1Ref.current);
				scrollTimeout1Ref.current = null;
			}
			if (scrollTimeout2Ref.current) {
				clearTimeout(scrollTimeout2Ref.current);
				scrollTimeout2Ref.current = null;
			}
			if (rafIdRef.current) {
				cancelAnimationFrame(rafIdRef.current);
				rafIdRef.current = null;
			}
		};
	});

	return (
		<StyledContainer ref={containerRef}>
			<StyledBackButtonContainer>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height40}
					borderRadius={ButtonBorderRadius.Radius5}
					style={{ width: 170 }}
					onClick={() => {
						dispatch(setIsOpeningCase(false));
						dispatch(setOpenCaseResult(null));
					}}
				>
					<StyledButtonContent style={{ gap: 5 }}>
						<img src={arrowLeft} style={{ width: 14, height: 14 }} />
						<Text>Вернуться назад</Text>
					</StyledButtonContent>
				</Button>
			</StyledBackButtonContainer>
			<StyledCount>
				<Text>
					{showReward ? (
						<span style={{ fontWeight: 500 }}>Ваша награда</span>
					) : (
						<>
							<span style={{ opacity: 0.5 }}>В наличии</span>{' '}
							<span style={{ fontWeight: 700 }}>{ownedCount ?? 0}</span>{' '}
							<span>
								{numWord(ownedCount ?? 0, ['кейс', 'кейса', 'кейсов'])}
							</span>
						</>
					)}
				</Text>
			</StyledCount>
			<StyledRewardShadow
				$animate={showReward}
				style={{
					opacity: showReward ? 1 : 0,
					backgroundColor:
						CaseItemColors[
							activeCase?.items.find((item) => item.donateItemId === rewardId)
								?.rarity ?? CaseItemRarity.COMMON
						],
					...glowAnimVars,
				}}
			/>
			<StyledCenterArrows style={{ opacity: showReward ? 0 : 1 }} />
			<StyledItemsContainer
				ref={itemsContainerRef}
				style={{
					transform: `translateX(${translateX})`,
					transition: instantMode
						? 'none'
						: `transform ${transitionTime}s ${transitionEasing}`,
				}}
			>
				{items.map((item, index) => {
					const isWinning = item.isWinningItem;
					const tiltTransform =
						isWinning && showReward
							? `perspective(1000px) rotateX(${tiltStyle.rotateX}deg) rotateY(${tiltStyle.rotateY}deg) scale(${tiltStyle.scale})`
							: undefined;

					const reward = rage.cases.getRewardById(item.itemValue as string);
					let amount = 0;
					if (item.type === RewardType.EXP) {
						amount =
							(reward.value as ParseRewardValueTypeMap[RewardType.EXP]) ?? 0;
					}
					if (item.type === RewardType.MONEY) {
						amount =
							(reward.value as ParseRewardValueTypeMap[RewardType.MONEY]) ?? 0;
					}

					const getLayerTransform = (
						intensity: number,
						centerType: 'none' | 'both' | 'x' = 'none',
					) => {
						if (!isWinning || !showReward) return undefined;
						const moveX = tiltStyle.mouseX * 2 * intensity;
						const moveY = tiltStyle.mouseY * 2 * intensity;

						if (centerType === 'both') {
							return `translate(calc(-50% + ${moveX}px), calc(-50% + ${moveY}px), 1px)`;
						}

						if (centerType === 'x') {
							return `translate(calc(-50% + ${moveX}px), ${moveY}px, 1px)`;
						}
						return `translate(${moveX}px, ${moveY}px, 1px)`;
					};

					if (item.isSecret) {
						return (
							<SecretRewardCard
								key={index}
								item={item}
								showReward={showReward}
								isFlipped={isFlipped}
								tiltTransform={tiltTransform}
								getLayerTransform={getLayerTransform}
								glowAnimVars={glowAnimVars}
								amount={amount}
							/>
						);
					}

					return (
						<StyledItem
							key={index}
							$isDimmed={showReward && !item.isWinningItem}
							$borderColor={isWinning && showReward ? item.color : undefined}
							style={{
								transform: tiltTransform,
								transition: 'transform 0.1s ease-out',
							}}
						>
							{isWinning && showReward && <StyledLight />}
							<StyledShadow
								$animate={isWinning && showReward}
								style={{
									backgroundColor: item.color,
									transform: getLayerTransform(0.3, 'none'),
									transition: 'transform 0.1s ease-out',
									...glowAnimVars,
								}}
							/>
							<StyledShadowCenter
								$animate={isWinning && showReward}
								style={{
									transform: getLayerTransform(0.2, 'both'),
									transition: 'transform 0.1s ease-out',
									...glowAnimVars,
								}}
							/>
							<StyledStarBg
								$animate={isWinning && showReward}
								style={{
									transform: getLayerTransform(0.4, 'both'),
									transition: 'transform 0.1s ease-out',
									...glowAnimVars,
								}}
							/>
							<StyledItemTopContainer
								style={{
									transform: getLayerTransform(0.5, 'none'),
									transition: 'transform 0.1s ease-out',
								}}
							>
								<div className="dot" style={{ backgroundColor: item.color }} />
								<Text>{item.name}</Text>
							</StyledItemTopContainer>
							<StyledImageContainer
								$type={item.type}
								style={{
									transform: getLayerTransform(0.6, 'x'),
									transition: 'transform 0.1s ease-out',
								}}
							>
								{item.type === RewardType.VEHICLE &&
								item.itemValue &&
								typeof item.itemValue === 'string' ? (
									<CollectionImage
										collection={'vehicles'}
										id={item.itemValue}
										size={'md'}
									/>
								) : null}
								{item.type === RewardType.ITEM &&
								item.itemValue &&
								typeof item.itemValue === 'string' &&
								isClothesItem(item.itemValue) ? (
									<ClothesImage id={item.itemValue} size={180} />
								) : null}
								{item.type === RewardType.ITEM &&
								item.itemValue &&
								typeof item.itemValue === 'string' &&
								!isClothesItem(item.itemValue) ? (
									<CollectionImage
										collection={'items'}
										id={item.itemValue}
										size={180}
									/>
								) : null}
								{item.type === RewardType.MONEY && (
									<img src={cardSoft} width={160} height={160} />
								)}
								{item.type === RewardType.EXP && (
									<StyledExpContainer>
										<ImgIcon
											icon={star}
											width={30}
											height={30}
											color="#FFB500"
										/>
										<Text>{amount.toLocaleString('ru-RU')}</Text>
									</StyledExpContainer>
								)}
							</StyledImageContainer>
						</StyledItem>
					);
				})}
			</StyledItemsContainer>
			<StyledButtonContainer>
				{isScrolling ? (
					<Button
						color={ButtonColors.Select2}
						height={ButtonHeight.Height50}
						onClick={handleFastScroll}
					>
						<StyledButtonContent>
							<img src={chest} />
							<Text>Быстрая прокрутка</Text>
						</StyledButtonContent>
					</Button>
				) : showReward ? (
					<StyledRewardButtonsGrid
						$canBeSold={
							rage.inventoryDonate.getPriceById(rewardId ?? '') !== null
						}
					>
						<Button
							color={ButtonColors.Select2}
							height={ButtonHeight.Height50}
							onClick={handleTakeReward}
						>
							<StyledButtonContent>
								<img src={hand} />
								<Text>Забрать награду</Text>
							</StyledButtonContent>
						</Button>
						{rage.inventoryDonate.getPriceById(rewardId ?? '') !== null && (
							<Button
								color={ButtonColors.Buy}
								height={ButtonHeight.Height50}
								onClick={handleSellReward}
							>
								<StyledButtonContent>
									<Text>Продать</Text>
									<CurrencyIcon
										icon={donateIcon}
										size="lg"
										asMask
										color="#FFD035"
									/>
									<Text>
										{rage.inventoryDonate
											.getPriceById(rewardId ?? '')
											?.toLocaleString('ru-RU')}
									</Text>
								</StyledButtonContent>
							</Button>
						)}
						<Button
							color={ButtonColors.Default}
							height={ButtonHeight.Height50}
							onClick={handleSkipReward}
						>
							<StyledButtonContent>
								<img src={box} />
								<Text>Отложить награду</Text>
							</StyledButtonContent>
						</Button>
					</StyledRewardButtonsGrid>
				) : !rewardId && ownedCount > 0 ? (
					<Button
						color={ButtonColors.Select2}
						height={ButtonHeight.Height50}
						onClick={() =>
							handleOpenCase().catch((err) =>
								rage.notifications.showException(err),
							)
						}
					>
						<StyledButtonContent>
							<img src={chest} />
							<Text>Крутить кейс</Text>
						</StyledButtonContent>
					</Button>
				) : (
					<Button
						color={ButtonColors.Select2}
						height={ButtonHeight.Height50}
						onClick={handleOpen}
						disabled={!rewardId}
					>
						<StyledButtonContent>
							<img src={chest} />
							<Text>Крутить кейс</Text>
						</StyledButtonContent>
					</Button>
				)}
			</StyledButtonContainer>
		</StyledContainer>
	);
};

export { ShopOpenCaseScroll };
